import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, BookOpen } from "lucide-react";
import { NEW_TESTAMENT, OLD_TESTAMENT, slugify } from "../data/books";
import { hasChapter } from "../data/chapters";

const stripPrefix = (name) => name.replace(/^st\.?\s+/i, "");

export default function SearchBar() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const out = [];
    const all = [
      ...NEW_TESTAMENT.map((b) => ({ ...b, testament: "New Testament" })),
      ...OLD_TESTAMENT.map((b) => ({ ...b, testament: "Old Testament" })),
    ];

    const m = term.match(/^(.*?)\s+(\d{1,3})$/);
    const bookPart = (m ? m[1] : term).trim();
    const chapNum = m ? parseInt(m[2], 10) : null;

    for (const b of all) {
      if (b.name.toLowerCase().includes(bookPart)) {
        const slug = slugify(stripPrefix(b.name));
        if (chapNum && chapNum >= 1 && chapNum <= b.chapters) {
          out.push({
            type: "chapter",
            label: `${b.name} ${chapNum}`,
            sub:
              b.testament +
              (hasChapter(slug, chapNum) ? " · transcribed" : ""),
            transcribed: hasChapter(slug, chapNum),
            to: `/book/${slug}/chapter/${chapNum}`,
          });
        } else {
          out.push({
            type: "book",
            label: b.name,
            sub: `${b.testament} · ${b.chapters} chapters`,
            to: `/book/${slug}/introduction`,
          });
        }
      }
    }
    return out.slice(0, 8);
  }, [q]);

  const submitFullText = (term) => {
    const t = (term ?? q).trim();
    if (!t) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(t)}`);
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="search-wrapper">
      <Search
        size={18}
        style={{
          position: "absolute",
          left: "0.85rem",
          top: "50%",
          transform: "translateY(-50%)",
          color: "var(--ink-muted)",
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitFullText();
        }}
      >
        <input
          data-testid="search-input"
          className="parchment-input"
          placeholder="Search the commentary (e.g. “Augustine”, “Matt 5:8”, “Eucharist”)"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitFullText();
            }
          }}
          autoComplete="off"
          autoCapitalize="none"
          spellCheck="false"
          inputMode="search"
          enterKeyHint="search"
        />
      </form>
      {open && q.trim() && (
        <ul
          data-testid="search-results"
          className="parchment-card"
          style={{
            position: "absolute",
            zIndex: 30,
            left: 0,
            right: 0,
            top: "calc(100% + 6px)",
            borderRadius: 3,
            maxHeight: 380,
            overflowY: "auto",
            padding: "0.35rem 0",
          }}
        >
          {results.map((r, i) => (
            <li key={`${r.to}-${i}`}>
              <Link
                to={r.to}
                onClick={() => {
                  setOpen(false);
                  setQ("");
                }}
                data-testid={`search-result-${i}`}
                style={{
                  display: "block",
                  padding: "0.55rem 0.9rem",
                  color: "var(--ink)",
                }}
              >
                <div style={{ fontSize: "1.02rem", fontWeight: 600 }}>
                  {r.label}
                  {r.transcribed && (
                    <span
                      style={{
                        color: "var(--gold)",
                        fontSize: "0.78rem",
                        marginLeft: "0.4rem",
                        fontStyle: "italic",
                      }}
                    >
                      ✦
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "var(--ink-muted)",
                    fontStyle: "italic",
                  }}
                >
                  {r.sub}
                </div>
              </Link>
            </li>
          ))}
          <li
            style={{
              borderTop: "1px solid var(--rule-soft)",
              marginTop: results.length > 0 ? "0.3rem" : 0,
            }}
          >
            <button
              type="button"
              onClick={() => submitFullText()}
              data-testid="search-fulltext-cta"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                width: "100%",
                padding: "0.65rem 0.9rem",
                textAlign: "left",
                background: "transparent",
                border: "none",
                color: "var(--oxblood)",
                cursor: "pointer",
                fontFamily: "'IM Fell English SC', serif",
                fontSize: "0.85rem",
                letterSpacing: "0.06em",
              }}
            >
              <BookOpen size={14} />
              Search the entire commentary for “{q.trim()}” &rarr;
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
